/**
 * The Risk assessment tab of /validation — Veeva version-change risk assessment.
 *
 * Deliberately **not** a chat: pick two files, press Run, read the report. One
 * batch operation, no follow-up turns, no conversation state. A version
 * comparison is a document you file, not a conversation you have.
 *
 * The report can be copied, downloaded as .txt, or written back to Blob as .txt.
 *
 * Two things this page shows that are easy to leave out and expensive to omit:
 * what extraction actually found in each file (so the operator can see it was
 * read properly before trusting a word of the report), and the provenance of
 * each run (model, prompt version, and a content hash per input).
 */

import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCloudUpload,
  IconFileTypePdf,
  IconInfoCircle,
  IconPlayerPlay,
  IconTrash,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { statusColor } from "@/shared/ui/palette";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { listConnections, type ConnectionOut } from "@/shared/api/integration";
import {
  deleteAssessment,
  getAssessment,
  listAssessments,
  runAssessment,
  saveAssessmentToBlob,
  type AssessmentOut,
  type ExtractionSummary,
} from "@/shared/api/ai";
import { BlobFilePicker, type BlobSelection } from "./BlobFilePicker";
import { ReportView } from "./ReportView";

/** Kept in step with `services/document_extract.py::SUPPORTED_EXTENSIONS`. */
const ACCEPTED = [
  ".pdf",
  ".docx",
  ".xlsx",
  // Macro-enabled Excel — what Veeva's Configuration Report exports as.
  ".xlsm",
  ".csv",
  ".tsv",
  ".txt",
  ".md",
  ".json",
  ".jsonl",
  ".xml",
  ".html",
] as const;

interface Deployment {
  name: string;
  label?: string;
  model?: string;
  model_version?: string;
}

function deploymentsOf(connection: ConnectionOut | undefined): Deployment[] {
  const raw = ((connection?.config ?? {}) as Record<string, unknown>).deployments;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (d): d is Deployment =>
      typeof d === "object" && d !== null && typeof (d as Deployment).name === "string",
  );
}

export function RiskAssessment() {
  const getToken = useAccessToken();

  const [aiConnections, setAiConnections] = useState<ConnectionOut[] | null>(null);
  const [blobConnections, setBlobConnections] = useState<ConnectionOut[] | null>(null);
  const [aiConnectionId, setAiConnectionId] = useState<string | null>(null);
  const [deployment, setDeployment] = useState<string | null>(null);
  const [blobConnectionId, setBlobConnectionId] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [current, setCurrent] = useState<BlobSelection[]>([]);
  const [upcoming, setUpcoming] = useState<BlobSelection[]>([]);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AssessmentOut | null>(null);
  const [history, setHistory] = useState<AssessmentOut[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  // Where the report gets written. Folder comes from a picker so any container
  // is reachable, not just whichever one the inputs happened to live in.
  const [saveFolder, setSaveFolder] = useState<BlobSelection | null>(null);
  const [saveFilename, setSaveFilename] = useState("");

  const loadConnections = useCallback(async () => {
    try {
      const token = await getToken();
      const [ai, blob, past] = await Promise.all([
        listConnections(token, "ai_foundry"),
        listConnections(token, "blob"),
        listAssessments(token),
      ]);
      setAiConnections(ai.items);
      setBlobConnections(blob.items);
      setHistory(past.items);
      // Sensible defaults so the common case is two clicks, not six.
      const firstAi = ai.items[0];
      setAiConnectionId(firstAi?.connection_id ?? null);
      setDeployment(deploymentsOf(firstAi)[0]?.name ?? null);
      setBlobConnectionId(blob.items[0]?.connection_id ?? null);
    } catch (err) {
      toastApiError("Could not load connections", err);
      setAiConnections([]);
      setBlobConnections([]);
    }
  }, [getToken]);

  useEffect(() => {
    void loadConnections();
  }, [loadConnections]);

  const onAiConnectionChange = (next: string | null) => {
    setAiConnectionId(next);
    const conn = aiConnections?.find((c) => c.connection_id === next);
    setDeployment(deploymentsOf(conn)[0]?.name ?? null);
  };

  const ready = Boolean(
    title.trim() &&
    blobConnectionId &&
    current.length > 0 &&
    upcoming.length > 0 &&
    aiConnectionId &&
    deployment &&
    !running,
  );

  /** Add or remove one file from a side. Compared on container+path because the
   *  same filename can legitimately exist in two containers. */
  const toggle = (
    setter: React.Dispatch<React.SetStateAction<BlobSelection[]>>,
    file: BlobSelection,
  ) =>
    setter((prev) =>
      prev.some((f) => f.container === file.container && f.path === file.path)
        ? prev.filter((f) => !(f.container === file.container && f.path === file.path))
        : [...prev, file],
    );

  const onRun = async () => {
    if (
      !ready ||
      current.length === 0 ||
      upcoming.length === 0 ||
      !blobConnectionId ||
      !aiConnectionId ||
      !deployment
    )
      return;
    setRunning(true);
    setFailure(null);
    setResult(null);
    try {
      const token = await getToken();
      const assessment = await runAssessment(token, {
        title: title.trim(),
        blob_connection_id: blobConnectionId,
        current: current.map((f) => ({ container: f.container, path: f.path })),
        upcoming: upcoming.map((f) => ({ container: f.container, path: f.path })),
        ai_connection_id: aiConnectionId,
        deployment,
      });
      setResult(assessment);
      setSaveFilename(`${slug(assessment.title)}.md`);
      const refreshed = await listAssessments(token);
      setHistory(refreshed.items);
    } catch (err) {
      // These failures are the useful ones — an unreadable file or oversized
      // input — so surface the message in place rather than only as a toast.
      setFailure(err instanceof Error ? err.message : "The assessment failed.");
      toastApiError("Assessment failed", err);
    } finally {
      setRunning(false);
    }
  };

  const onOpenPast = async (id: string) => {
    try {
      const token = await getToken();
      const assessment = await getAssessment(token, id);
      setResult(assessment);
      setFailure(null);
      setSaveFilename(`${slug(assessment.title)}.md`);
    } catch (err) {
      toastApiError("Could not open that assessment", err);
    }
  };

  /** Write the report into Blob, as Markdown or as a PDF rendered from it.
   *
   *  The filename extension follows the format rather than the text box, so a
   *  PDF cannot end up named `.md` — the operator picks the format by which
   *  button they press, and the name follows. */
  const onSaveToBlob = async (fmt: "md" | "pdf") => {
    if (!result || !saveFolder || !saveFilename.trim()) return;
    const typed = saveFilename.trim();
    const stem = typed.replace(/\.(md|txt|pdf)$/i, "");
    try {
      const token = await getToken();
      const updated = await saveAssessmentToBlob(token, result.assessment_id, {
        blob_connection_id: saveFolder.container ? blobConnectionId : null,
        container: saveFolder.container,
        path: `${saveFolder.path}${stem}.${fmt}`,
        fmt,
      });
      setResult(updated);
      toastSuccess(
        fmt === "pdf" ? "Saved to Blob as PDF" : "Saved to Blob",
        updated.saved_blob_path ?? `${stem}.${fmt}`,
      );
    } catch (err) {
      toastApiError("Could not save to Blob", err);
    }
  };

  const onDelete = async (row: AssessmentOut) => {
    // A reason is required because the row goes — after that the audit event is
    // the only account of why. Same discipline as a connection delete.
    const reason = await promptForReason("Delete assessment", "Audit reason", {
      confirmLabel: "Delete",
    });
    if (!reason) return;
    try {
      const token = await getToken();
      await deleteAssessment(token, row.assessment_id, reason);
      if (result?.assessment_id === row.assessment_id) setResult(null);
      const refreshed = await listAssessments(token);
      setHistory(refreshed.items);
      toastSuccess("Assessment deleted", `${row.title} was removed.`);
    } catch (err) {
      toastApiError("Could not delete", err);
    }
  };

  const noAi = aiConnections !== null && aiConnections.length === 0;
  const noBlob = blobConnections !== null && blobConnections.length === 0;
  const deployments = deploymentsOf(aiConnections?.find((c) => c.connection_id === aiConnectionId));

  return (
    <Stack gap="md">
      {/* No heading — the hub above owns it. What the page does is evident from
          the two labelled file pickers; the sentence that used to describe them
          said it a second time. */}
      {(noAi || noBlob) && (
        <Alert variant="light" color="yellow" icon={<IconInfoCircle size={18} />}>
          {noAi
            ? "This needs an Azure AI Foundry connection. Add one under Integrations."
            : "This needs an Azure Blob connection holding the two documents."}
        </Alert>
      )}

      <Paper withBorder radius="md" p="lg">
        <Stack gap="md">
          <TextInput
            label="Title"
            description="How this assessment will appear in the list below."
            placeholder="Vault 26R2 upgrade — Quality"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
          />

          <Group grow align="flex-start">
            <Select
              label="AI connection"
              value={aiConnectionId}
              onChange={onAiConnectionChange}
              data={(aiConnections ?? []).map((c) => ({
                value: c.connection_id,
                label: c.display_name,
              }))}
              allowDeselect={false}
            />
            <Select
              label="Model"
              value={deployment}
              onChange={setDeployment}
              data={deployments.map((d) => ({
                value: d.name,
                label: d.label ?? d.model ?? d.name,
              }))}
              allowDeselect={false}
              disabled={deployments.length === 0}
            />
            <Select
              label="Blob connection"
              value={blobConnectionId}
              onChange={(next) => {
                setBlobConnectionId(next);
                setCurrent([]);
                setUpcoming([]);
              }}
              data={(blobConnections ?? []).map((c) => ({
                value: c.connection_id,
                label: c.display_name,
              }))}
              allowDeselect={false}
            />
          </Group>

          {blobConnectionId && (
            <Group grow align="flex-start">
              <BlobFilePicker
                connectionId={blobConnectionId}
                label="Current configuration"
                value={null}
                onChange={() => undefined}
                values={current}
                onToggle={(f) => toggle(setCurrent, f)}
                acceptedExtensions={ACCEPTED}
              />
              <BlobFilePicker
                connectionId={blobConnectionId}
                label="Upcoming configuration"
                value={null}
                onChange={() => undefined}
                values={upcoming}
                onToggle={(f) => toggle(setUpcoming, f)}
                acceptedExtensions={ACCEPTED}
              />
            </Group>
          )}

          <Group justify="flex-end">
            <Button
              leftSection={<IconPlayerPlay size={16} />}
              onClick={() => void onRun()}
              loading={running}
              disabled={!ready}
            >
              Run assessment
            </Button>
          </Group>

          {running && (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Reading both documents and assessing the change — this can take a minute for large
                files.
              </Text>
            </Group>
          )}

          {failure && (
            <Alert
              variant="light"
              color="red"
              icon={<IconAlertTriangle size={18} />}
              title="The assessment did not run"
            >
              <Text size="sm">{failure}</Text>
            </Alert>
          )}
        </Stack>
      </Paper>

      {result && (
        <ReportPanel
          result={result}
          blobConnectionId={blobConnectionId}
          saveFolder={saveFolder}
          onSaveFolderChange={setSaveFolder}
          saveFilename={saveFilename}
          onSaveFilenameChange={setSaveFilename}
          onSaveToBlob={(fmt) => void onSaveToBlob(fmt)}
        />
      )}

      {history.length > 0 && (
        <Paper withBorder radius="md" p="md">
          <Text fw={600} mb="xs">
            Past assessments
          </Text>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>When</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>By</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {history.map((row) => (
                <Table.Tr
                  key={row.assessment_id}
                  style={{ cursor: "pointer" }}
                  onClick={() => void onOpenPast(row.assessment_id)}
                >
                  <Table.Td>{row.title}</Table.Td>
                  <Table.Td>{new Date(row.created_at_utc).toLocaleString()}</Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={statusColor(row.status)}>
                      {row.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{row.actor_display_name ?? row.actor_email ?? "—"}</Table.Td>
                  <Table.Td>
                    <Tooltip label="Delete (reason required)">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Delete ${row.title}`}
                        onClick={(event) => {
                          // The row itself opens the report — don't do both.
                          event.stopPropagation();
                          void onDelete(row);
                        }}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

interface ReportPanelProps {
  result: AssessmentOut;
  blobConnectionId: string | null;
  saveFolder: BlobSelection | null;
  onSaveFolderChange: (value: BlobSelection | null) => void;
  saveFilename: string;
  onSaveFilenameChange: (value: string) => void;
  onSaveToBlob: (fmt: "md" | "pdf") => void;
}

function ReportPanel({
  result,
  blobConnectionId,
  saveFolder,
  onSaveFolderChange,
  saveFilename,
  onSaveFilenameChange,
  onSaveToBlob,
}: ReportPanelProps) {
  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>{result.title}</Text>
            <Text size="xs" c="dimmed">
              {result.model_reported ?? result.deployment} · prompt {result.prompt_version}
              {result.duration_ms ? ` · ${(result.duration_ms / 1000).toFixed(1)}s` : ""}
            </Text>
          </div>
        </Group>

        <ExtractionPanel result={result} />

        <Alert variant="light" color="yellow" icon={<IconInfoCircle size={18} />}>
          <Text size="sm">
            AI-generated decision support. Review every finding against the source documents before
            acting on it or filing it under change control.
          </Text>
        </Alert>

        {result.report_text ? (
          <ScrollArea.Autosize mah={700}>
            <ReportView text={result.report_text} />
          </ScrollArea.Autosize>
        ) : (
          <Alert variant="light" color="red">
            {result.error ?? "No report was produced."}
          </Alert>
        )}

        <Divider />

        <Text fw={600} size="sm">
          Save to Blob
        </Text>
        {blobConnectionId && (
          <BlobFilePicker
            connectionId={blobConnectionId}
            label="Destination folder"
            mode="folder"
            value={saveFolder}
            onChange={onSaveFolderChange}
          />
        )}
        <Group align="flex-end" gap="xs">
          <TextInput
            label="Filename"
            value={saveFilename}
            onChange={(e) => onSaveFilenameChange(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button
            variant="light"
            leftSection={<IconCloudUpload size={16} />}
            onClick={() => onSaveToBlob("md")}
            disabled={!result.report_text || !saveFolder || !saveFilename.trim()}
          >
            Save .md
          </Button>
          <Tooltip label="Rendered from the Markdown, tables and all">
            <Button
              variant="light"
              leftSection={<IconFileTypePdf size={16} />}
              onClick={() => onSaveToBlob("pdf")}
              disabled={!result.report_text || !saveFolder || !saveFilename.trim()}
            >
              Save as PDF
            </Button>
          </Tooltip>
        </Group>
        {saveFolder && saveFilename.trim() && (
          <Text size="xs" c="dimmed">
            Will write to{" "}
            <Code>
              {saveFolder.container}/{saveFolder.path}
              {saveFilename.trim().replace(/\.(md|txt|pdf)$/i, "")}
            </Code>
            <Text span size="xs" c="dimmed">
              {" "}
              plus <Code>.md</Code> or <Code>.pdf</Code> — whichever button you press.
            </Text>
          </Text>
        )}
        {result.saved_blob_path && (
          <Text size="xs" c="dimmed">
            Saved to <Code>{result.saved_blob_path}</Code>
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

/** What the extractor found per file. Shown because a report is only as good as
 *  its inputs, and a half-read file is otherwise invisible. */
function ExtractionPanel({ result }: { result: AssessmentOut }) {
  const sides: [string, ExtractionSummary[]][] = [
    ["Current", result.current_inputs ?? []],
    ["Upcoming", result.upcoming_inputs ?? []],
  ];
  const total = sides.reduce((n, [, files]) => n + files.length, 0);
  // An assessment written before migration 0019 has empty lists. Say so rather
  // than rendering an empty box that looks like nothing was read.
  if (total === 0) {
    return (
      <Paper withBorder radius="sm" p="xs" bg="var(--mantine-color-default-hover)">
        <Text size="xs" c="dimmed">
          Per-file detail was not recorded for this assessment.
        </Text>
      </Paper>
    );
  }
  return (
    <Paper withBorder radius="sm" p="xs" bg="var(--mantine-color-default-hover)">
      <Text size="xs" fw={600} mb={4}>
        What was read ({total} file{total === 1 ? "" : "s"})
      </Text>
      <Stack gap={4}>
        {sides.map(([side, files]) =>
          files.length === 0 ? null : (
            <div key={side}>
              <Text size="xs" fw={600} c="dimmed">
                {side}
              </Text>
              <Stack gap={2}>
                {files.map((info, i) => (
                  <Text size="xs" c="dimmed" key={info.path ?? info.filename ?? i}>
                    {info.filename} ({info.format}, {(info.chars ?? 0).toLocaleString()} chars
                    {info.parts?.length ? `, ${info.parts.join(", ")}` : ""})
                    {info.skipped?.length ? ` - skipped: ${info.skipped.join(", ")}` : ""}
                    {info.sha256 ? ` - sha256 ${info.sha256.slice(0, 12)}` : ""}
                  </Text>
                ))}
              </Stack>
            </div>
          ),
        )}
      </Stack>
    </Paper>
  );
}

function slug(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "risk-assessment"
  );
}
